import { useTranslations } from 'next-intl';
import {
  type ChangeEvent,
  type MouseEvent,
  useState,
} from 'react';

import { REPORT_REASONS } from '@/features/listing/constants/listing-report-constants';
import { type Listing } from '@/features/listing/types/listing-types';
import sendUserReport from '@/features/user/requests/send-user-report';
import { type UserReportType } from '@/features/user/types/user-report-types';
import { type User } from '@/features/user/types/user-types';
import Button from '@/ui/button/button';
import Toggle from '@/ui/form-elements/toggle/toggle';
import { type ModalOldProps } from '@/ui/modal-old/modal-old';
import ModalOld from '@/ui/modal-old/modal-old';

import styles from './listing-report-modal.module.scss';

interface ListingReportModalProps extends ModalOldProps {
  userId: User['id'];
  listingId?: Listing['id'];
}

export default function ListingReportModal(props: ListingReportModalProps) {
  const {
    isActive,
    listingId,
    onClose,
    userId,
  } = props;

  const listingReportModalTranslations = useTranslations('listing.modal.report');
  const actionsTranslations = useTranslations('global.actions');

  const [message,
    setMessage] = useState('');
  const [selectedReason,
    setSelectedReason] = useState<UserReportType>('CommercialPosting');
  const [isSuccessSent,
    setIsSuccessSent] = useState(false);
  const [isSending,
    setIsSending] = useState(false);

  const closeModalAfterSuccessTime = 2000;

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  const handleReportReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedReason(event.target.value as UserReportType);
  };

  const handleMessageSend = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setIsSending(true);

    const text = listingId ? `[Listing ID: ${String(listingId)}] - ${message}` : `[User report] - ${message}`;

    sendUserReport({
      reportText: text,
      reportType: selectedReason,
      userId,
    })
      .then(() => {
        setMessage('');
        setIsSuccessSent(true);
        setTimeout(() => {
          onClose();
        }, closeModalAfterSuccessTime);
      })
      .catch(() => null)
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <ModalOld onClose={onClose} isActive={isActive}>
      <div className={styles.listingReportModal}>
        <p className={styles.listingReportModal_title}>
          {listingReportModalTranslations('title')}
        </p>

        <div className={styles.listingReportModal_reasonsList}>
          {REPORT_REASONS.map(({
            translateToken,
            type,
          }) => (
            <Toggle
              onChange={handleReportReasonChange}
              type="radio"
              id={type}
              name="report-reason"
              value={type}
              checked={selectedReason === type}
              variant="button"
              key={type}
            >
              {listingReportModalTranslations(`report-options.${translateToken}`)}
            </Toggle>
          ))}
        </div>

        <textarea
          placeholder={listingReportModalTranslations('placeholder')}
          className={styles.listingReportModal_message}
          value={message}
          onChange={handleMessageChange}
        />

        <div className={styles.listingReportModal_sendMessage}>
          <Button
            element="button"
            size="md"
            fontSize="xs"
            onClick={handleMessageSend}
            fullWidth
            isLoading={isSending}
            isSuccess={isSuccessSent}
            successMessage={actionsTranslations('sent')}
          >
            {actionsTranslations('send-message')}
          </Button>
        </div>
      </div>
    </ModalOld>
  );
}
